import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSessionStatus } from '../redux/features/sessionBoundary/sessionBoundary.selectors';
import { SessionStatus } from '../redux/features/sessionBoundary/sessionBoundary.constants';
import { addSlashCommands } from '../reducers/dialogSlice';
import { selectDialog } from '../selectors/dialog';
import { SlashCommand } from '../types/menu';

interface ReminderContextType {
  showReminder: (message: string, slashCommands?: SlashCommand[]) => void;
  hideReminder: () => void;
  reminderMessage: string;
  isVisible: boolean;
}

const ReminderContext = createContext<ReminderContextType | undefined>(
  undefined
);

interface ReminderProviderProps {
  children: ReactNode;
}

export const ReminderProvider: React.FC<ReminderProviderProps> = ({
  children,
}) => {
  const [reminderMessage, setReminderMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const sessionStatus = useSelector(selectSessionStatus);
  const dialogState = useSelector(selectDialog);
  const dispatch = useDispatch();

  const showReminder = (message: string, slashCommands?: SlashCommand[]) => {
    setReminderMessage(message);
    setIsVisible(true);

    // Add slash commands to separate array if provided and not already present
    if (slashCommands?.length && dialogState.slashCommands.length === 0) {
      dispatch(addSlashCommands(slashCommands));
    }
    // If slash commands already exist, do nothing - they should only be processed once
  };

  const hideReminder = () => {
    setIsVisible(false);
  };

  // Auto-hide reminder when a new session starts
  useEffect(() => {
    if (sessionStatus === SessionStatus.start && isVisible) {
      hideReminder();
    }
  }, [sessionStatus]);

  return (
    <ReminderContext.Provider
      value={{
        showReminder,
        hideReminder,
        reminderMessage,
        isVisible,
      }}
    >
      {children}
    </ReminderContext.Provider>
  );
};

export const useReminder = (): ReminderContextType => {
  const context = useContext(ReminderContext);
  if (context === undefined) {
    throw new Error('useReminder must be used within a ReminderProvider');
  }
  return context;
};
